/**
 * Recursively sorts object keys for consistent serialization.
 * Handles nested objects and arrays.
 */
export function sortObjectKeys(obj) {
    if (obj === null || typeof obj !== 'object') {
        return obj;
    }
    if (Array.isArray(obj)) {
        return obj.map(item => sortObjectKeys(item));
    }
    const sortedKeys = Object.keys(obj).sort();
    const sortedObj = {};
    for (const key of sortedKeys) {
        sortedObj[key] = sortObjectKeys(obj[key]);
    }
    return sortedObj;
}
/**
 * Generates a cache key from URL and params for request deduplication.
 * Ensures consistent keys regardless of object property order.
 */
export function generateCacheKey(url, params) {
    const sortedParams = JSON.stringify(sortObjectKeys(params));
    return `${url || ''}:${sortedParams}`;
}
export class RequestDeduplicator {
    constructor(options = {}) {
        this.inflightRequests = {};
        this.concurrentRequestCounts = {};
        this.onConcurrentRequests = options.onConcurrentRequests;
        this.concurrentThreshold = options.concurrentThreshold ?? 2;
    }
    deduplicate(key, request) {
        if (!key) {
            return request();
        }
        if (this.inflightRequests[key]) {
            this.concurrentRequestCounts[key] =
                (this.concurrentRequestCounts[key] || 1) + 1;
            return this.inflightRequests[key];
        }
        this.concurrentRequestCounts[key] = 1;
        const currentMap = this.inflightRequests;
        const currentCounts = this.concurrentRequestCounts;
        this.inflightRequests[key] = (async () => {
            try {
                return await request();
            }
            finally {
                if (currentCounts[key] > this.concurrentThreshold &&
                    this.onConcurrentRequests) {
                    this.onConcurrentRequests(key, currentCounts[key]);
                }
                delete currentMap[key];
                delete currentCounts[key];
            }
        })();
        return this.inflightRequests[key];
    }
    isInflight(key) {
        return !!this.inflightRequests[key];
    }
    clear() {
        this.inflightRequests = {};
        this.concurrentRequestCounts = {};
    }
}
//# sourceMappingURL=deduplicateRequest.js.map